import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useLocation, Link } from 'react-router-dom';
import { ListPlus, X, Camera, Loader2, Check, Trash2, ExternalLink } from 'lucide-react';
import { toast } from 'react-hot-toast';
import { useAuth } from '../../context/AuthContext';
import { kisitliTip } from '../RestrictedSidebar';

// Sağ kenarda duran "Task" kulakçığı: her sayfada görünür, tıklanınca o an seçili
// markanın taskboard'una görev açan küçük panel açılır. Sayfa linki açıklamaya
// otomatik eklenir; ekran görüntüsü isteğe bağlı.

const API = (() => {
  const raw = (process.env.REACT_APP_API_URL || 'https://api.glowytics.com').trim();
  return raw.replace(/\/+$/, '').replace(/\/api$/, '');
})();

const DEMO_BRAND_ID = 'ec9873c2-0edb-44eb-ac6f-2670c8e56dc4';
const BOARD_KEY = (brandId) => `taskboard:selectedBoardId:${brandId}`;
const WIDGET_ATTR = 'data-quick-task';

const getAuthHeaders = () => {
  const token =
    localStorage.getItem('token') ||
    localStorage.getItem('authToken') ||
    localStorage.getItem('accessToken') ||
    document.cookie.replace(/(?:(?:^|.*;\s*)token\s*=\s*([^;]*).*$)|^.*$/, '$1');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const apiFetch = async (url, options = {}) => {
  const res = await fetch(url, {
    credentials: 'include',
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...getAuthHeaders(),
      ...(options.headers || {}),
    },
  });
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  return res.json();
};

const EMPTY_FORM = { title: '', description: '', assigned_to: '', priority: 3, due_date: '' };

const QuickTaskTab = () => {
  const { selectedBrand } = useAuth();
  const location = useLocation();
  const brandId = selectedBrand?.id;

  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [users, setUsers] = useState([]);
  const [boards, setBoards] = useState([]);
  const [boardId, setBoardId] = useState('');
  const [shot, setShot] = useState(null); // JPEG data URL
  const [capturing, setCapturing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [created, setCreated] = useState(null);
  const titleRef = useRef(null);

  const hidden =
    !brandId ||
    brandId === DEMO_BRAND_ID ||
    selectedBrand?.currentUserRole === 'BRANDING' ||
    kisitliTip(selectedBrand);

  // Marka değişince formu sıfırla; liste verilerini ilk açılışta çek.
  useEffect(() => {
    setUsers([]);
    setBoards([]);
    setBoardId('');
    setCreated(null);
  }, [brandId]);

  useEffect(() => {
    if (!open || hidden || boards.length) return;
    apiFetch(`${API}/api/tasks/boards?brandId=${brandId}`)
      .then((d) => {
        const list = (d.boards || []).filter((b) => !b.is_archived);
        setBoards(list);
        let saved = null;
        try { saved = localStorage.getItem(BOARD_KEY(brandId)); } catch (_) { /* yok say */ }
        const pick = list.find((b) => b.id === saved) || list.find((b) => b.is_default) || list[0];
        setBoardId(pick ? pick.id : '');
      })
      .catch(() => {});
    apiFetch(`${API}/api/tasks/brand-users?brandId=${brandId}`)
      .then((d) => setUsers(d.users || []))
      .catch(() => {});
  }, [open, hidden, brandId, boards.length]);

  useEffect(() => {
    if (open) setTimeout(() => titleRef.current?.focus(), 50);
  }, [open]);

  // Esc ile kapat
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const captureScreen = useCallback(async () => {
    setCapturing(true);
    try {
      const { default: html2canvas } = await import('html2canvas');
      const canvas = await html2canvas(document.body, {
        x: window.scrollX,
        y: window.scrollY,
        width: window.innerWidth,
        height: window.innerHeight,
        scale: Math.min(window.devicePixelRatio || 1, 1.5),
        useCORS: true,
        logging: false,
        ignoreElements: (el) => el.hasAttribute && el.hasAttribute(WIDGET_ATTR),
      });
      setShot(canvas.toDataURL('image/jpeg', 0.8));
    } catch (err) {
      console.error('[QuickTask] screenshot failed:', err);
      toast.error('Could not capture the screen');
    } finally {
      setCapturing(false);
    }
  }, []);

  const reset = () => {
    setForm(EMPTY_FORM);
    setShot(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const title = form.title.trim();
    if (!title) { titleRef.current?.focus(); return; }
    setSaving(true);
    try {
      let shotUrl = null;
      if (shot) {
        const up = await apiFetch(`${API}/api/tasks/screenshot`, {
          method: 'POST',
          body: JSON.stringify({ brandId, image: shot }),
        });
        shotUrl = `${API}${up.path}`;
      }

      const pageUrl = window.location.href;
      const meta = [`Page: ${pageUrl}`];
      if (shotUrl) meta.push(`Screenshot: ${shotUrl}`);
      const description = [form.description.trim(), '—', ...meta].filter(Boolean).join('\n');

      const data = await apiFetch(`${API}/api/tasks`, {
        method: 'POST',
        headers: { 'X-Brand-ID': brandId },
        body: JSON.stringify({
          brandId,
          boardId: boardId || undefined,
          title,
          description,
          type: 'user',
          status: 'todo',
          priority: parseInt(form.priority, 10) || 3,
          assigned_to: form.assigned_to || null,
          due_date: form.due_date || null,
        }),
      });

      if (boardId) {
        try { localStorage.setItem(BOARD_KEY(brandId), boardId); } catch (_) { /* yok say */ }
      }
      window.dispatchEvent(new CustomEvent('quick-task-created', { detail: data.task }));
      setCreated(data.task || { title });
      reset();
    } catch (err) {
      console.error('[QuickTask] create failed:', err);
      toast.error('Could not create task');
    } finally {
      setSaving(false);
    }
  };

  if (hidden) return null;

  const boardHref = `/taskboard`;
  const onTaskBoard = location.pathname.endsWith('/taskboard');

  return (
    <div {...{ [WIDGET_ATTR]: '' }}>
      {/* Glowy Drop — kapalıyken sağ altta yüzen düz mavi yuvarlak düğme (gradyan reddedildi) */}
      {!open && (
        <button
          type="button"
          onClick={() => { setCreated(null); setOpen(true); }}
          title="Glowy Drop — drop a task to the board"
          className="fixed right-6 bottom-24 z-[60] group flex items-center gap-2.5 h-14 pl-2 pr-5
                     rounded-full text-white shadow-lg bg-blue-600 hover:bg-blue-700 transition-colors"
        >
          <span className="w-10 h-10 rounded-full bg-white/15 flex items-center justify-center">
            <ListPlus size={20} />
          </span>
          <span className="flex flex-col items-start leading-tight">
            <span className="text-sm font-bold tracking-tight">Glowy Drop</span>
            <span className="text-[10px] text-white/80 font-medium">New task</span>
          </span>
        </button>
      )}

      {open && (
        <div
          className="fixed right-4 bottom-6 z-[60] w-[360px] max-w-[calc(100vw-2rem)]
                     bg-white rounded-2xl shadow-2xl border border-gray-200 flex flex-col
                     max-h-[calc(100vh-3rem)]"
          role="dialog"
          aria-label="Create task"
        >
          <div className="flex items-center justify-between px-4 py-3 rounded-t-2xl text-white bg-blue-600">
            <div className="min-w-0">
              <div className="text-sm font-bold">Glowy Drop · New task</div>
              <div className="text-xs text-white/80 truncate">
                {selectedBrand?.name}
                {boards.length > 1 && boards.find((b) => b.id === boardId)
                  ? ` · ${boards.find((b) => b.id === boardId).name}`
                  : ''}
              </div>
            </div>
            <button
              type="button"
              onClick={() => setOpen(false)}
              className="p-1.5 rounded-lg text-white/80 hover:text-white hover:bg-white/20"
              aria-label="Close"
            >
              <X size={16} />
            </button>
          </div>

          {created ? (
            <div className="p-5 flex flex-col items-center text-center gap-3">
              <div className="w-10 h-10 rounded-full bg-emerald-50 text-emerald-600 flex items-center justify-center">
                <Check size={20} />
              </div>
              <div className="text-sm text-gray-800">
                <span className="font-medium">{created.title}</span> added to the board.
              </div>
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => setCreated(null)}
                  className="px-3 py-1.5 text-sm rounded-lg bg-gray-900 text-white hover:bg-black"
                >
                  Add another
                </button>
                {!onTaskBoard && (
                  <Link
                    to={boardHref}
                    onClick={() => setOpen(false)}
                    className="px-3 py-1.5 text-sm rounded-lg bg-gray-100 text-gray-700 hover:bg-gray-200 inline-flex items-center gap-1"
                  >
                    Open board <ExternalLink size={13} />
                  </Link>
                )}
              </div>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="flex flex-col min-h-0">
              <div className="p-4 space-y-3 overflow-y-auto">
                <input
                  ref={titleRef}
                  value={form.title}
                  onChange={set('title')}
                  placeholder="What needs to be done?"
                  maxLength={255}
                  className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-900/10 focus:border-gray-400"
                />
                <textarea
                  value={form.description}
                  onChange={set('description')}
                  placeholder="Details (optional)"
                  rows={3}
                  className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg resize-none focus:outline-none focus:ring-2 focus:ring-gray-900/10 focus:border-gray-400"
                />

                <div className="grid grid-cols-2 gap-2">
                  <label className="block">
                    <span className="block text-[11px] font-medium text-gray-500 mb-1">Assignee</span>
                    <select
                      value={form.assigned_to}
                      onChange={set('assigned_to')}
                      className="w-full px-2 py-1.5 text-sm border border-gray-200 rounded-lg bg-white"
                    >
                      <option value="">Unassigned</option>
                      {users.map((u) => (
                        <option key={u.id} value={u.id}>{u.name || u.email}</option>
                      ))}
                    </select>
                  </label>
                  <label className="block">
                    <span className="block text-[11px] font-medium text-gray-500 mb-1">Priority</span>
                    <select
                      value={form.priority}
                      onChange={set('priority')}
                      className="w-full px-2 py-1.5 text-sm border border-gray-200 rounded-lg bg-white"
                    >
                      <option value={1}>High</option>
                      <option value={2}>Medium</option>
                      <option value={3}>Low</option>
                    </select>
                  </label>
                  <label className="block">
                    <span className="block text-[11px] font-medium text-gray-500 mb-1">Due date</span>
                    <input
                      type="date"
                      value={form.due_date}
                      onChange={set('due_date')}
                      className="w-full px-2 py-1.5 text-sm border border-gray-200 rounded-lg bg-white"
                    />
                  </label>
                  {boards.length > 1 ? (
                    <label className="block">
                      <span className="block text-[11px] font-medium text-gray-500 mb-1">Board</span>
                      <select
                        value={boardId}
                        onChange={(e) => setBoardId(e.target.value)}
                        className="w-full px-2 py-1.5 text-sm border border-gray-200 rounded-lg bg-white"
                      >
                        {boards.map((b) => (
                          <option key={b.id} value={b.id}>{b.name}</option>
                        ))}
                      </select>
                    </label>
                  ) : <div />}
                </div>

                {shot ? (
                  <div className="relative rounded-lg overflow-hidden border border-gray-200">
                    <img src={shot} alt="Screenshot" className="w-full block" />
                    <button
                      type="button"
                      onClick={() => setShot(null)}
                      className="absolute top-1.5 right-1.5 p-1 rounded-md bg-white/90 text-gray-600 hover:text-red-600 shadow"
                      aria-label="Remove screenshot"
                    >
                      <Trash2 size={14} />
                    </button>
                  </div>
                ) : (
                  <button
                    type="button"
                    onClick={captureScreen}
                    disabled={capturing}
                    className="w-full flex items-center justify-center gap-2 py-2 text-sm text-gray-600 border border-dashed border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-60"
                  >
                    {capturing ? <Loader2 size={14} className="animate-spin" /> : <Camera size={14} />}
                    {capturing ? 'Capturing…' : 'Attach screenshot of this page'}
                  </button>
                )}

                <div className="text-[11px] text-gray-400 truncate" title={location.pathname}>
                  Page link is added automatically · {location.pathname}
                </div>
              </div>

              <div className="px-4 py-3 border-t border-gray-100 flex justify-end">
                <button
                  type="submit"
                  disabled={saving || !form.title.trim()}
                  className="px-4 py-2 text-sm font-medium rounded-lg bg-gray-900 text-white hover:bg-black disabled:opacity-40 inline-flex items-center gap-1.5"
                >
                  {saving && <Loader2 size={14} className="animate-spin" />}
                  Create task
                </button>
              </div>
            </form>
          )}
        </div>
      )}
    </div>
  );
};

export default QuickTaskTab;
